// fe-ios12/src/components/InstallPrompt.js
// Slim top banner — "Open in App for better experience" (like Amazon).
// — Android Chrome 68+: native install via beforeinstallprompt (captured in index.js)
// — iOS/iPadOS Safari: step-by-step "Add to Home Screen" guide (expandable)
// — Already in standalone mode (installed PWA): hidden
import React, { useState, useEffect } from 'react';

var DISMISS_KEY  = 'myc_pwa_dismissed_at';
var DISMISS_DAYS = 7;

function lsGet(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, String(val)); } catch (e) { /* ignore */ }
}

function detectIOS() {
  var ua = navigator.userAgent || '';
  var classicIOS = /iphone|ipad|ipod/i.test(ua);
  var modernIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  var isApple = classicIOS || modernIPadOS;
  var notThirdPartyBrowser = !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return { ios: isApple && notThirdPartyBrowser, ipad: /ipad/i.test(ua) || modernIPadOS };
}

function detectAndroid() {
  var ua = navigator.userAgent || '';
  if (!/android/i.test(ua)) return { android: false, browser: '' };
  if (/SamsungBrowser/i.test(ua)) return { android: true, browser: 'samsung' };
  if (/Firefox/i.test(ua)) return { android: true, browser: 'firefox' };
  if (/OPR\//i.test(ua)) return { android: true, browser: 'opera' };
  if (/Chrome/i.test(ua)) return { android: true, browser: 'chrome' };
  return { android: true, browser: 'other' };
}

function isInStandaloneMode() {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch (e) { /* ignore */ }
  return window.navigator.standalone === true;
}

export default function InstallPrompt() {
  var [deferredPrompt, setDeferredPrompt] = useState(null);
  var [show,           setShow]           = useState(false);
  var [iosMode,        setIosMode]        = useState(false);
  var [androidManual,  setAndroidManual]  = useState(false); // Android without beforeinstallprompt
  var [androidBrowser, setAndroidBrowser] = useState('');
  var [guideExpanded,  setGuideExpanded]  = useState(false);
  var [isIpad,         setIsIpad]         = useState(false);
  var [installing,     setInstalling]     = useState(false);

  useEffect(function () {
    if (isInStandaloneMode()) return;

    var ts = lsGet(DISMISS_KEY);
    if (ts && Date.now() - Number(ts) < DISMISS_DAYS * 86400 * 1000) return;

    var detected = detectIOS();
    if (detected.ios) {
      setIosMode(true);
      setIsIpad(detected.ipad);
      setShow(true);
      return;
    }

    // Check if beforeinstallprompt was already captured globally (fired before mount)
    if (window.__pwaInstallPrompt) {
      setDeferredPrompt(window.__pwaInstallPrompt);
      setShow(true);
    }

    // Also listen for future fires (e.g., after app uninstall or new criteria met)
    function handlePrompt(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      window.__pwaInstallPrompt = e;
      setShow(true);
    }
    function handleInstalled() { setShow(false); window.__pwaInstallPrompt = null; }
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);

    // Android fallback: if beforeinstallprompt hasn't fired after 3s, show manual guide
    var androidInfo = detectAndroid();
    var fallbackTimer = null;
    if (androidInfo.android && !window.__pwaInstallPrompt) {
      fallbackTimer = setTimeout(function () {
        if (!window.__pwaInstallPrompt) {
          setAndroidManual(true);
          setAndroidBrowser(androidInfo.browser);
          setShow(true);
        }
      }, 3000);
    }

    return function () {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  function handleInstall() {
    var prompt = deferredPrompt || window.__pwaInstallPrompt;
    if (!prompt) return;
    setInstalling(true);
    prompt.prompt();
    prompt.userChoice.then(function (result) {
      setDeferredPrompt(null);
      window.__pwaInstallPrompt = null;
      setInstalling(false);
      if (result.outcome === 'accepted') setShow(false);
    });
  }

  function handleDismiss() {
    lsSet(DISMISS_KEY, Date.now());
    setShow(false);
  }

  if (!show) return null;

  return (
    <>
      <style>{`
        .ip-top-banner {
          position: sticky; top: 0; left: 0; right: 0; z-index: 9999;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-bottom: 1px solid rgba(56,189,248,0.25);
          padding: 8px 12px;
          display: -webkit-box; display: -webkit-flex; display: flex;
          -webkit-box-align: center; -webkit-align-items: center; align-items: center;
          gap: 10px;
          -webkit-animation: ipFadeIn 0.3s ease; animation: ipFadeIn 0.3s ease;
        }
        @keyframes ipFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .ip-top-icon {
          width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
          border: 1.5px solid rgba(56,189,248,0.4);
        }
        .ip-top-text { flex: 1; min-width: 0; }
        .ip-top-title { color: #f1f5f9; font-size: 13px; font-weight: 700; line-height: 1.2; }
        .ip-top-sub { color: #64748b; font-size: 11px; margin-top: 1px; }
        .ip-top-btn {
          background: #38bdf8; color: #0f172a; border: none; border-radius: 8px;
          font-size: 12px; font-weight: 800; padding: 7px 14px; cursor: pointer;
          white-space: nowrap; flex-shrink: 0;
        }
        .ip-top-btn:disabled { opacity: 0.6; }
        .ip-top-close {
          background: none; border: none; color: #475569; font-size: 16px;
          cursor: pointer; padding: 2px 4px; flex-shrink: 0; line-height: 1;
        }
        .ip-ios-steps {
          background: #0f172a; border-bottom: 1px solid rgba(56,189,248,0.15);
          padding: 10px 16px 14px; color: #cbd5e1; font-size: 13px;
        }
        .ip-ios-step { padding: 6px 0; display: flex; gap: 8px; align-items: flex-start; }
        .ip-ios-num { font-weight: 800; color: #38bdf8; min-width: 18px; }
      `}</style>

      <div className="ip-top-banner">
        <img src="/myc.png" alt="MYC" className="ip-top-icon" />
        <div className="ip-top-text">
          <div className="ip-top-title">Medical Yoga Centre</div>
          <div className="ip-top-sub">Open in App for better experience</div>
        </div>
        {(iosMode || androidManual) ? (
          <button className="ip-top-btn" onClick={function () { setGuideExpanded(!guideExpanded); }}>
            Get MYC
          </button>
        ) : (
          <button className="ip-top-btn" onClick={handleInstall} disabled={installing}>
            {installing ? 'Installing...' : 'Get MYC'}
          </button>
        )}
        <button className="ip-top-close" onClick={handleDismiss} aria-label="Dismiss">&times;</button>
      </div>

      {iosMode && guideExpanded && (
        <div className="ip-ios-steps">
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#f1f5f9' }}>
            Add to Home Screen in 3 steps:
          </div>
          <div className="ip-ios-step">
            <span className="ip-ios-num">1.</span>
            <span>
              Tap the <strong style={{ color: '#38bdf8' }}>Share</strong> button{' '}
              {isIpad ? '(top-right corner)' : '(bottom toolbar)'}
            </span>
          </div>
          <div className="ip-ios-step">
            <span className="ip-ios-num">2.</span>
            <span>Scroll down, tap <strong style={{ color: '#38bdf8' }}>"Add to Home Screen"</strong></span>
          </div>
          <div className="ip-ios-step">
            <span className="ip-ios-num">3.</span>
            <span>Tap <strong style={{ color: '#38bdf8' }}>Add</strong> — MYC icon appears on your home screen!</span>
          </div>
        </div>
      )}

      {androidManual && guideExpanded && (
        <div className="ip-ios-steps">
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#f1f5f9' }}>
            Install MYC App:
          </div>
          {androidBrowser === 'samsung' ? (
            <>
              <div className="ip-ios-step">
                <span className="ip-ios-num">1.</span>
                <span>Tap the <strong style={{ color: '#38bdf8' }}>menu icon</strong> (3 lines, bottom-right)</span>
              </div>
              <div className="ip-ios-step">
                <span className="ip-ios-num">2.</span>
                <span>Tap <strong style={{ color: '#38bdf8' }}>"Add page to"</strong> then <strong style={{ color: '#38bdf8' }}>"Home screen"</strong></span>
              </div>
            </>
          ) : androidBrowser === 'firefox' ? (
            <>
              <div className="ip-ios-step">
                <span className="ip-ios-num">1.</span>
                <span>Tap the <strong style={{ color: '#38bdf8' }}>menu</strong> (3 dots, top-right)</span>
              </div>
              <div className="ip-ios-step">
                <span className="ip-ios-num">2.</span>
                <span>Tap <strong style={{ color: '#38bdf8' }}>"Install"</strong> or <strong style={{ color: '#38bdf8' }}>"Add to Home screen"</strong></span>
              </div>
            </>
          ) : (
            <>
              <div className="ip-ios-step">
                <span className="ip-ios-num">1.</span>
                <span>Tap the <strong style={{ color: '#38bdf8' }}>menu</strong> (3 dots, top-right)</span>
              </div>
              <div className="ip-ios-step">
                <span className="ip-ios-num">2.</span>
                <span>Tap <strong style={{ color: '#38bdf8' }}>"Install app"</strong> or <strong style={{ color: '#38bdf8' }}>"Add to Home screen"</strong></span>
              </div>
            </>
          )}
          <div className="ip-ios-step">
            <span className="ip-ios-num">{androidBrowser === 'samsung' ? '3' : '3'}.</span>
            <span>Tap <strong style={{ color: '#38bdf8' }}>Install/Add</strong> — MYC icon appears on your home screen!</span>
          </div>
        </div>
      )}
    </>
  );
}
